import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense, Fragment } from "react";
import { TOAST_CLOSE_BUTTONS } from "@/utils/toast";
import { useParams, useSearchParams } from "react-router-dom";
import { loadJsPDF } from "@/utils/pdfEngines";
import { IonSkeletonText, IonToast } from "@ionic/react";
import AppLayout from "@/components/AppLayout";
import { renderLayout, fetchPublishedLayout, inferBaseDocType } from "@/utils/layoutEngine";
import { generateAllPreviewImages, SAMPLE_PAYSTUB_DATA } from "@/utils/paystubPreviewGenerator";
import { generateAllCanadianPreviewImages, SAMPLE_CANADIAN_PAYSTUB_DATA } from "@/utils/canadianPaystubPreviewGenerator";
import { generateAllUkPreviewImages, SAMPLE_UK_PAYSLIP_DATA } from "@/utils/ukPayslipPreviewGenerator";
import { pdfToPageImages } from "@/utils/pdfPageImages";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
import { buildCustomDocFormConfig } from "@/utils/customDocForm";
import AppTaxFormModal from "./AppTaxFormModal";
import { BUSINESS_FORM_CONFIGS } from "./appBusinessFormConfigs";
import { LEGAL_FORM_CONFIGS } from "./appLegalFormConfigs";
import { getSampleVariants } from "@/utils/layoutEngine";

import FreshOverlay from "@/components/FreshOverlay";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// The real generators, opened embedded (form modal only, preselected on the
// business's design) so a template made from a built-in is filled with that
// generator's own form, computation and preview flow, right here in the
// business's category. Never sent to the built-in generator's page.
const EmbeddedPaystub = lazy(() => import("./AppPaystub"));
const EmbeddedCanadianPaystub = lazy(() => import("./AppCanadianPaystub"));
const EmbeddedUkPayslip = lazy(() => import("./AppUkPayslip"));
const EmbeddedOfferLetter = lazy(() => import("./AppOfferLetter"));
const EmbeddedResumeBuilder = lazy(() => import("./AppResumeBuilder"));
const DEFAULT_PRICE = 9.99;
// The built-ins a template can be designed from that have a generator of their
// own. Anything else (a legal agreement, a blank canvas) is a free-form design
// whose form is built from the fields its layout uses.
// The config-driven forms (legal and business), keyed by the base type a
// design can be made from; each opens with its design preset in the field
// that picks the style.
const FORM_CONFIGS = { ...LEGAL_FORM_CONFIGS, ...BUSINESS_FORM_CONFIGS };
const DESIGN_FIELD = { "bank-statement": "bankId", "cease-and-desist": "template", "vehicle-bill-of-sale": "template", "utility-bill": "providerId" };
const GENERATOR_BASES = new Set(["paystub", "canadian-paystub", "uk-payslip", "offer-letter", "resume", ...Object.keys(FORM_CONFIGS)]);
const CARD_ACTION = {
  paystub: "Enter Pay Data", "canadian-paystub": "Enter Pay Data", "uk-payslip": "Enter Pay Data",
  "offer-letter": "Create Offer Letter", resume: "Build Resume", "bank-statement": "Enter Statement Data",
};
const cardAction = (base) => CARD_ACTION[base] || (FORM_CONFIGS[base] ? `Create ${FORM_CONFIGS[base].title}` : null);
const sampleForBase = (base) => (base === "canadian-paystub" ? SAMPLE_CANADIAN_PAYSTUB_DATA : base === "uk-payslip" ? SAMPLE_UK_PAYSLIP_DATA : SAMPLE_PAYSTUB_DATA);
// The generator template string for a template: the business's own serve from
// the authed endpoint ("usercustom:"), the admin's from the public one.
const templateRef = (t) => `${t && t._own ? "usercustom" : "custom"}:${t && t.id}`;
const templatePrice = (t) => {
  const p = Number(t?.price);
  return Number.isFinite(p) && p >= 0.5 ? p : DEFAULT_PRICE;
};

// A custom category page (an admin's or a business's own): every published
// template assigned to this category shows as a card. Tapping one opens the
// modal of the generator the design was made from, preselected on it, or, for
// a free-form design, the same form-then-preview modal with fields built from
// the layout.
export default function AppCustomDocs({ embedDocumentType = null, embedTemplateId = null, onEmbedClose = null } = {}) {
  // Embedded mode: open one template's fill-in modal in place (e.g. from the
  // Business generator) instead of navigating to this page.
  const embedded = !!embedTemplateId;
  const { slug: paramSlug } = useParams();
  const slug = embedDocumentType || paramSlug;
  const [searchParams] = useSearchParams();
  const wantTemplateId = embedTemplateId || searchParams.get("template"); // deep-link / embed: open this template

  const [categoryLabel, setCategoryLabel] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [previews, setPreviews] = useState({});          // templateId -> card image
  const [layouts, setLayouts] = useState({});            // templateId -> published layout
  const [baseTypes, setBaseTypes] = useState({});        // templateId -> base sniffed from the layout
  const [open, setOpen] = useState(null);                // { template, base, variants } while a modal is open
  const [toastState, setToastState] = useState({ isOpen: false, message: "", color: "danger" });
  const showToast = (message, color = "danger") => setToastState({ isOpen: true, message, color });

  // A template's base document type: the server's value if it sends one, else
  // sniffed from the loaded layout, else the built-in type an admin filed it
  // under. It decides which generator's modal the card opens.
  const baseOf = (t) => (t && (t.baseDocType || baseTypes[t.id] || (GENERATOR_BASES.has(t.documentType) ? t.documentType : ""))) || "";

  // A category holds distinct FORMS, each of which can have multiple visual
  // template variants: templates sharing a formGroup are one card with a
  // switchable Template picker; templates without one are their own card.
  const groupKey = (t) => (t.formGroup || "").trim() || `solo:${t.id}`;
  const formGroups = [];
  for (const t of templates) {
    const key = groupKey(t);
    const g = formGroups.find((x) => x.key === key);
    if (g) g.templates.push(t);
    else formGroups.push({ key, name: (t.formGroup || "").trim() || t.name, templates: [t] });
  }

  // ── load category label + its published templates ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoadingTemplates(true);
    setTemplates([]);
    const isUserCat = slug.startsWith("ucat-");
    const catUrl = isUserCat ? `${BACKEND_URL}/api/user/doc-template-categories` : `${BACKEND_URL}/api/doc-template-categories`;
    fetch(catUrl, isUserCat ? { headers: userHeaders() } : undefined)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const cat = (d.categories || []).find((c) => c.slug === slug);
        setCategoryLabel(cat?.label || "Documents");
      })
      .catch(() => {});

    // A user category shows the business's own templates; an admin category
    // shows the admin's, plus any the business filed under it (owner-only).
    const ownP = fetch(`${BACKEND_URL}/api/user/doc-templates?documentType=${encodeURIComponent(slug)}&published=1`, { headers: userHeaders() })
      .then((r) => r.json())
      .then((d) => (d && d.success && d.hasAccess ? (d.templates || []) : []).map((t) => ({ ...t, _own: true })))
      .catch(() => []);
    const adminP = isUserCat
      ? Promise.resolve([])
      : fetch(`${BACKEND_URL}/api/doc-templates?documentType=${encodeURIComponent(slug)}`)
          .then((r) => r.json())
          .then((d) => (d.templates || []).map((t) => ({ ...t, _own: false })))
          .catch(() => []);
    Promise.all([adminP, (isUserCat || isUserLoggedIn()) ? ownP : Promise.resolve([])])
      .then(([a, o]) => { if (!cancelled) setTemplates([...a, ...o]); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingTemplates(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const loadLayout = useCallback(async (templateId) => {
    if (layouts[templateId]) return layouts[templateId];
    const t = templates.find((x) => x.id === templateId);
    const own = t ? !!t._own : slug.startsWith("ucat-");
    const layout = await fetchPublishedLayout(templateId, undefined, { user: own });
    setLayouts((prev) => ({ ...prev, [templateId]: layout }));
    return layout;
  }, [layouts, slug, templates]);

  // ── card previews: first page of each template rendered with sample data ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const t of templates) {
        if (previews[t.id]) continue;
        try {
          const layout = await loadLayout(t.id);
          if (cancelled || !layout) continue;
          const base = t.baseDocType || inferBaseDocType(layout);
          setBaseTypes((prev) => (prev[t.id] === base ? prev : { ...prev, [t.id]: base }));
          let img;
          if (base === "paystub" || base === "canadian-paystub" || base === "uk-payslip") {
            // A paystub design: render a fully computed sample stub through the
            // paystub preview generator, so the thumbnail matches the real doc.
            const gen = base === "canadian-paystub" ? generateAllCanadianPreviewImages : base === "uk-payslip" ? generateAllUkPreviewImages : generateAllPreviewImages;
            const imgs = await gen(sampleForBase(base), templateRef(t), 1);
            img = imgs && imgs[0];
          } else if (GENERATOR_BASES.has(base)) {
            // a generator's design previews with that generator's sample data
            const doc = new (await loadJsPDF())({ unit: "pt", format: "letter" });
            renderLayout(doc, layout, getSampleVariants(base)[0].data, base);
            const imgs = await pdfToPageImages(doc.output("arraybuffer"));
            img = imgs && imgs[0];
          } else {
            const doc = new (await loadJsPDF())({ unit: "pt", format: "letter" });
            renderLayout(doc, layout, {}, slug, { raw: true });
            const imgs = await pdfToPageImages(doc.output("arraybuffer"));
            img = imgs && imgs[0];
          }
          if (cancelled) return;
          if (img) setPreviews((prev) => ({ ...prev, [t.id]: img }));
        } catch (e) { console.error("Custom template preview failed:", e); }
      }
    })();
    return () => { cancelled = true; };
  }, [templates]); // eslint-disable-line

  // ── open a template's modal ───────────────────────────────────────────────
  const openTemplate = async (templateId) => {
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    const group = formGroups.find((g) => g.key === groupKey(t));
    const variants = group ? group.templates : [t];
    const base = baseOf(t);
    // a free-form design's form comes from its layout, so every style in the
    // group loads first (switching styles in the form keeps what was typed)
    if (!GENERATOR_BASES.has(base)) {
      try {
        const loaded = await Promise.all(variants.map((v) => loadLayout(v.id)));
        if (!loaded[variants.indexOf(t)]) throw new Error("Template is not published");
      } catch { showToast("Could not load this template"); return; }
    }
    setOpen({ template: t, base, variants });
  };
  const closeOpen = () => { setOpen(null); if (embedded && onEmbedClose) onEmbedClose(); };

  // A deep link (…?template=<id>) opens that template's modal once the list
  // has loaded; the fixed generators hand a custom design off here this way.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || !wantTemplateId || open) return;
    if (templates.some((t) => t.id === wantTemplateId)) { autoOpenedRef.current = true; openTemplate(wantTemplateId); }
  }, [wantTemplateId, templates, open]); // eslint-disable-line

  // A free-form design: the same form-then-preview modal as the legal and
  // business forms, with fields built from the layout. A business's own
  // template is in its plan, so it is never priced.
  const freeFormConfig = open && !GENERATOR_BASES.has(open.base)
    ? buildCustomDocFormConfig({
        slug, template: open.template, variants: open.variants, layouts,
        groupKey: groupKey(open.template),
        groupName: (formGroups.find((g) => g.key === groupKey(open.template)) || {}).name || open.template.name,
        templateRef, price: templatePrice, included: !!open.template._own,
      })
    : null;

  // a form generator's config for the open design; a business's own design
  // is part of its plan
  const formConfig = useMemo(() => (open && FORM_CONFIGS[open.base] ? { ...FORM_CONFIGS[open.base], included: !!open.template._own } : null), [open]);
  const designField = open ? (DESIGN_FIELD[open.base] || "design") : "design";

  const Wrap = embedded ? Fragment : AppLayout;
  const wrapProps = embedded ? {} : { fillHeight: true };
  return (
    <Wrap {...wrapProps}>
      {!embedded && (
      <div style={{ padding: 10, height: "100%", boxSizing: "border-box" }}>
        <div style={{ background: "var(--ion-card-background)", borderRadius: 6, padding: "20px 20px 24px", height: "100%", overflowY: "auto", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", boxSizing: "border-box" }}>
          {!loadingTemplates && templates.length === 0 && (
            <p style={{ color: "var(--ion-color-medium)", fontSize: "0.9rem", textAlign: "center", padding: "32px 0" }}>
              No templates are currently available in this category.
            </p>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
            {(loadingTemplates ? [] : formGroups).map((g, cardIdx) => {
              const first = g.templates[0];
              const base = baseOf(first);
              // a generator's own price applies to designs made from it; a
              // business's own template is in its plan
              const showPrice = !first._own && !GENERATOR_BASES.has(base);
              return (
                <div key={g.key} className="app-sample-card ion-activatable"
                  onClick={() => openTemplate(first.id)}
                  style={{ animationDelay: `${cardIdx * 60}ms`, cursor: "pointer", borderRadius: 10, border: "1.5px solid var(--app-divider, rgba(0,0,0,0.12))", background: "var(--ion-card-background)", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "box-shadow 0.2s, transform 0.15s", position: "relative" }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
                >
                  <ion-ripple-effect />
                  <div style={{ position: "relative", background: "#fff", overflow: "hidden", minHeight: 160 }}>
                    <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: first.badgeColor || "#34d399", color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,0.28)" }}>
                      {g.name}
                    </div>
                    {g.templates.length > 1 && (
                      <div style={{ position: "absolute", top: 10, right: 10, zIndex: 2, background: "rgba(15,23,42,0.75)", color: "#fff", padding: "4px 10px", borderRadius: 999, fontSize: "0.68rem", fontWeight: 700 }}>
                        {g.templates.length} styles
                      </div>
                    )}
                    {previews[first.id] ? (
                      <div style={{ position: "relative", paddingTop: "129.4%", overflow: "hidden", pointerEvents: "none" }}>
                        <img src={previews[first.id]} alt={`${g.name} template`} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
                        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 56, background: "linear-gradient(to top, rgba(0,0,0,0.28), transparent)" }} />
                      </div>
                    ) : (
                      <div style={{ position: "relative", paddingTop: "129.4%", overflow: "hidden" }}>
                        <IonSkeletonText animated={true} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", margin: 0, borderRadius: 0, "--background": "rgba(0, 0, 0, 0.085)", "--background-rgb": "18, 18, 18" }} />
                      </div>
                    )}
                  </div>
                  <div style={{ padding: "8px 16px 12px", textAlign: "center", borderTop: "1px solid var(--app-divider, rgba(0,0,0,0.06))" }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--ion-color-primary)" }}>
                      {cardAction(base) || (g.templates.length > 1 ? "Choose a Style" : "Select Template")}{showPrice ? ` ($${Math.min(...g.templates.map(templatePrice)).toFixed(2)})` : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* ── The generator's own modal, preselected on this design ── */}
      {open && (open.base === "paystub" || open.base === "canadian-paystub" || open.base === "uk-payslip") && (
        <Suspense fallback={null}>
          {open.base === "canadian-paystub"
            ? <EmbeddedCanadianPaystub embedTemplate={templateRef(open.template)} onEmbedClose={closeOpen} />
            : open.base === "uk-payslip"
              ? <EmbeddedUkPayslip embedTemplate={templateRef(open.template)} onEmbedClose={closeOpen} />
              : <EmbeddedPaystub embedTemplate={templateRef(open.template)} onEmbedClose={closeOpen} />}
        </Suspense>
      )}
      {open && open.base === "offer-letter" && (
        <Suspense fallback={null}>
          <EmbeddedOfferLetter isOpen embedTemplate={templateRef(open.template)} onClose={closeOpen} />
        </Suspense>
      )}
      {open && open.base === "resume" && (
        <Suspense fallback={null}>
          <EmbeddedResumeBuilder isOpen embedTemplate={templateRef(open.template)} embedTemplateName={open.template.name} onClose={closeOpen} />
        </Suspense>
      )}
      {formConfig && (
        <AppTaxFormModal
          config={formConfig}
          embedFields={{ [designField]: templateRef(open.template) }}
          embedOptions={{ [designField]: open.variants.map((v) => ({ value: templateRef(v), label: v.name })) }}
          onClose={closeOpen}
        />
      )}
      {freeFormConfig && (
        <AppTaxFormModal config={freeFormConfig} embedFields={{ __template: open.template.id }} onClose={closeOpen} />
      )}

      <FreshOverlay as={IonToast}
        buttons={TOAST_CLOSE_BUTTONS}
        isOpen={toastState.isOpen}
        message={toastState.message}
        color={toastState.color}
        duration={2600}
        onDidDismiss={() => setToastState((t) => ({ ...t, isOpen: false }))}
      />
    </Wrap>
  );
}
